/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W2 — GAS HEATER CMS-PATH RESOLVER (§33.5, §31).
 *
 * ★ W3 REUSE SEAM (uvcCms.ts §32): the universal CMS-doc accessor
 * `getFromCmsData(doc, dot-path, id)` — a verbatim port of legacy
 * `fn.tools.get_from_cms_data` — already lives in uvcCms.ts. W1.5 flagged that
 * accessor as vendor-agnostic and explicitly told W2-W6 to REUSE it and add
 * "a gas_heater path-map alongside, per the W3 reuse seam noted in uvcCms.ts."
 * This module does exactly that: it IMPORTS `getFromCmsData` from uvcCms and
 * adds the gas_heater-specific path map + label rules. Nothing is duplicated.
 *
 * WHY GAS HEATER NEEDS ITS OWN DOC + PATHS:
 *   The gas_heater selector reads TWO different option sources:
 *     1. The gas_heater CMS DOC (legacy `fn['current.gas_heater.cms']`,
 *        `model.accessory.gas_heater.cms` urlRoot
 *        `cms/1/v1.0.0/ahu/gas_heaters/params/`) — a static dictionary that
 *        carries, under the top-level `gas_heater` key:
 *          gas_heater.manufacturer      → mfg_id      (label: name)
 *          gas_heater.power             → power_id    (type-1 voltage; label: name)
 *          gas_heater.connection_hand   → conn_hand_id(label: name)
 *          gas_heater.certification     → certification_id (label: name)
 *          gas_heater.blank_off.selection → blank_off_id (label: name)
 *        The GLOBAL cms doc additionally carries:
 *          air.air_stream               → air_stream_id (label: name)
 *          weather_rating               → weather_rating_id (label: name)
 *     2. The PARAM-CHAIN GETs (`checkDataExist`:
 *        `cms/1/v1.0.0/ahu/gas_heaters/params/{type,series,heatex_mtl,…}/`) —
 *        DEPENDENT lists fetched per mfg/type/series. Those are NOT in the doc;
 *        they are loaded by gasHeaterSelectorApi + read live (see the selector).
 *
 *   So unlike UVC (where almost every dependent select resolved through the
 *   doc), gas_heater's doc only resolves the STATIC selects (mfg / voltage /
 *   conn-hand / certification / blank-off / air-stream / weather-rating). The
 *   chained selects (type/series/heatex_mtl/tube_rows/configuration/venting/
 *   pilot/fuel_type/firing_mode/burner_mtl/burner_config/heatex_qty/
 *   output_rating_std/qty) come from the param GETs and render with their own
 *   row shapes (id+name, or bare scalar arrays).
 *
 * NB the two docs share the resolver but live under different URLs; the
 * selector fetches the gas_heater doc (GH_CMS_DOC_URL) and the GLOBAL cms doc
 * (the board already has it) and passes whichever the path belongs to.
 */

import { getFromCmsData, type UvcCmsDoc } from './uvcCms';
import type { CmsNamedRow } from '../cmsDoc';
import type { OptionRow } from '../formKit';

/** The gas_heater CMS doc shape is loose (nested arrays under dot-paths). */
export type GasHeaterCmsDoc = UvcCmsDoc;

/**
 * The gas_heater CMS doc URL (legacy model.accessory.gas_heater.cms urlRoot).
 * Fetched via the shared getComponentCmsDoc RTKQ query (URL-keyed, 24h cache).
 * Same family the form's withoutRatingSeparateAPI render reads.
 */
export const GH_CMS_DOC_URL = '/api/v1.0/cms/1/v1.0.0/ahu/gas_heaters/params/';

/**
 * field-name → dot-path INTO THE GAS_HEATER CMS DOC (the doc whose top key is
 * `gas_heater`). Verbatim from the legacy gas_heater.selector.tmpl option
 * sources (`gas_heater.gas_heater.<x>` / `power_list`). Only the STATIC selects
 * are listed; the chained param selects are NOT doc-resolved.
 */
export const GH_DOC_PATHS: Record<string, string> = {
  mfg_id: 'gas_heater.manufacturer',
  power_id: 'gas_heater.power', // type-1 voltage list (type≠1 uses the voltages master collection)
  conn_hand_id: 'gas_heater.connection_hand',
  certification_id: 'gas_heater.certification',
  blank_off_id: 'gas_heater.blank_off.selection',
};

/**
 * field-name → dot-path INTO THE GLOBAL cms doc (the board's main CMS doc).
 * Verbatim from the tmpl (`cms.air.air_stream`, `weather_rating`).
 */
export const GH_GLOBAL_CMS_PATHS: Record<string, string> = {
  air_stream_id: 'air.air_stream',
  weather_rating_id: 'weather_rating',
};

/**
 * The option <text> for a gas_heater CMS row. Every doc-resolved gas_heater
 * select labels by `name` (the legacy tmpl uses `{{name}}` for ALL of them —
 * manufacturer/power/connection_hand/certification/blank_off/air_stream/
 * weather_rating). Falls through name → value → series → model defensively.
 */
export function ghCmsOptionLabel(row: CmsNamedRow | undefined): string {
  if (!row) return '';
  if (row.name != null && row.name !== '') return String(row.name);
  if (row.value != null && row.value !== '') return String(row.value);
  if (row.series != null && row.series !== '') return String(row.series);
  if (row.model != null && row.model !== '') return String(row.model);
  return '';
}

/** Pick the right path map for a doc-resolved field (gas_heater doc vs global). */
function pathFor(field: string): { path: string; scope: 'gh' | 'global' } | undefined {
  if (field in GH_DOC_PATHS) return { path: GH_DOC_PATHS[field], scope: 'gh' };
  if (field in GH_GLOBAL_CMS_PATHS)
    return { path: GH_GLOBAL_CMS_PATHS[field], scope: 'global' };
  return undefined;
}

/**
 * Resolve, for one doc-resolved gas_heater select, the {id,label} OPTION LIST.
 * value = row.id; label = ghCmsOptionLabel(row). REUSES uvcCms.getFromCmsData
 * (the §31 universal accessor) — `ids == null` → FULL list (read-only seeds the
 * full list, a superset, so the saved value always renders).
 */
export function ghCmsOptionList(
  ghDoc: GasHeaterCmsDoc | undefined,
  globalDoc: GasHeaterCmsDoc | undefined,
  field: string,
  ids?: Array<number | string> | null
): OptionRow[] {
  const p = pathFor(field);
  if (!p) return [];
  const doc = p.scope === 'gh' ? ghDoc : globalDoc;
  const result = getFromCmsData(doc, p.path, ids ?? null);
  const arr = Array.isArray(result) ? result : result ? [result] : [];
  return arr.map((row) => ({
    ...row,
    id: row.id,
    label: ghCmsOptionLabel(row),
  })) as OptionRow[];
}

/**
 * Resolve the DISPLAY LABEL for the currently-selected id of a doc-resolved
 * gas_heater select (legacy get_from_cms_data(path, selectedId, doc) →
 * ghCmsOptionLabel). Used for the selected-value text + read-back when the
 * option list is empty.
 */
export function ghCmsSelectedLabel(
  ghDoc: GasHeaterCmsDoc | undefined,
  globalDoc: GasHeaterCmsDoc | undefined,
  field: string,
  id: number | string | null | undefined
): string {
  if (id == null || id === '') return '';
  const p = pathFor(field);
  if (!p) return '';
  const doc = p.scope === 'gh' ? ghDoc : globalDoc;
  const row = getFromCmsData(doc, p.path, id);
  return ghCmsOptionLabel(Array.isArray(row) ? row[0] : row);
}

/** Is this field resolved via a CMS doc (vs a param-chain GET / master list)? */
export function isGhCmsResolvedField(field: string): boolean {
  return field in GH_DOC_PATHS || field in GH_GLOBAL_CMS_PATHS;
}
